import { useQuery } from '@apollo/client/react'
import isEmpty from 'lodash-es/isEmpty'
import React, { useMemo } from 'react'
import { useUpdateEffect } from 'react-use'

import { Dialog, LoadingSpinner } from '@syconium/little-miss-figgy/dist/components'

import { gql } from '../../../../__generated__/graphql/catalog'
import { reportClientError } from '../../../../app/_components/chrome/scripts/DataDogRumScript'
import { useColorGroupLabel } from '../../../../app/_hooks/useColorGroupLabel'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'

import GalleryTile from './ColorGalleryTile'
import * as Styles from './styles'

import type { FetchColorGalleryQuery } from '../../../../__generated__/graphql/catalog/graphql'

const colorGalleryQuery = gql(`
      query FetchColorGallery($handle: String!) {
        productCatalogStandardProduct(handle: $handle) {
          id
          handle
          variantGroups(first: 72) {
            totalCount
            nodes {
              id
              urlPath
              images(first: 1) {
                url
                altText
              }
              color {
                id
                displayValue
                group {
                  id
                }
              }
            }
          }
        }
      }
      `)

type ColorGalleryModalProps = {
  productHandle: string
  onSelectColor?: ((colorKey: string) => void) | undefined
}

const ColorGalleryModalContent = ({ productHandle, onSelectColor }: ColorGalleryModalProps) => {
  const {
    colorGallery: { colors },
  } = useTranslation()
  const translateColorGroupLabel = useColorGroupLabel()

  // TODO: This should have paging. We should not be loading products in bulk.
  const { data, error, loading } = useQuery(colorGalleryQuery, {
    skip: !productHandle,
    variables: { handle: productHandle },
  })

  useUpdateEffect(() => {
    if (!error) return
    reportClientError({
      error,
      context: { scope: 'Color Gallery Modal' },
    })
  }, [error])

  const colorGroups = useMemo(() => {
    const variantGroups = data?.productCatalogStandardProduct.variantGroups
    if (isEmpty(variantGroups)) return []

    const colorGroups: Record<
      string,
      {
        group: {
          id: string
        }
        variantGroups: Array<
          FetchColorGalleryQuery['productCatalogStandardProduct']['variantGroups']['nodes'][number]
        >
      }
    > = {}
    variantGroups.nodes.forEach(variantGroup => {
      if (variantGroup.color?.group) {
        const colorGroup = colorGroups[variantGroup.color.group.id] ?? {
          group: variantGroup.color.group,
          variantGroups: [],
        }
        colorGroup.variantGroups.push(variantGroup)
        colorGroups[variantGroup.color.group.id] = colorGroup
      }
    })
    return Object.values(colorGroups)
  }, [data?.productCatalogStandardProduct.variantGroups])

  return (
    <>
      {loading ? (
        <Styles.LoadingSpinnerWrapper>
          <LoadingSpinner label='Loading colors…' size='100px' />
        </Styles.LoadingSpinnerWrapper>
      ) : (
        <Styles.DialogContent>
          {colorGroups.map(colorGroup => (
            <React.Fragment key={colorGroup.group.id}>
              <Styles.DialogTitle>
                {translateColorGroupLabel(colorGroup.group.id)} {colors}
              </Styles.DialogTitle>
              <Styles.GalleryWrap>
                {colorGroup.variantGroups.map(variantGroup => {
                  if (!variantGroup.color || !variantGroup.images?.[0]?.url) return null
                  return (
                    <GalleryTile
                      key={variantGroup.id}
                      colorKey={variantGroup.color.id}
                      colorDisplayValue={variantGroup.color.displayValue}
                      pdpUrl={variantGroup.urlPath}
                      onSelectColor={onSelectColor}
                      imageUrl={variantGroup.images[0].url}
                      altText={variantGroup.images[0].altText}
                    />
                  )
                })}
              </Styles.GalleryWrap>
            </React.Fragment>
          ))}
        </Styles.DialogContent>
      )}
    </>
  )
}

const ColorGalleryModalHeader = ({ productHandle }: ColorGalleryModalProps) => {
  const {
    colorGallery: { colors, title },
  } = useTranslation()

  const { data } = useQuery(colorGalleryQuery, {
    skip: !productHandle,
    variables: { handle: productHandle },
  })

  return (
    <Dialog.Header
      title={
        <Styles.ModalTitleWrap>
          <Dialog.Title align='left'>
            <Styles.DialogHeader>{title}</Styles.DialogHeader>
          </Dialog.Title>
          {data?.productCatalogStandardProduct.variantGroups.totalCount && (
            <Styles.ColorCount>
              {data.productCatalogStandardProduct.variantGroups.totalCount} {colors}
            </Styles.ColorCount>
          )}
        </Styles.ModalTitleWrap>
      }
      dismiss={<Dialog.Dismiss aria-label='Close modal' />}
    />
  )
}

const ColorGalleryModal = (props: ColorGalleryModalProps) => {
  return (
    <Styles.DialogModal
      analyticsTitle='Color Gallery'
      fullscreenMobile
      unmountOnHide={true}
      header={<ColorGalleryModalHeader {...props} />}
      footer={null}
      content={<ColorGalleryModalContent {...props} />}
    ></Styles.DialogModal>
  )
}

export default ColorGalleryModal
